/** @jsxImportSource @emotion/react */
import React from 'react';
import { Box, Typography, Button, Grid } from '@mui/material';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setFloorLevel, nextStep, prevStep, setStepVerified } from '../habitationSlice';
import { FloorLevel } from '../types';
import FloorIcon from '../components/FloorIcon';
import InfoText from '../components/InfoText';
import { useTranslation } from 'react-i18next';

const OptionBox = styled(Box)<{ selected: boolean }>`
  border: 2px solid ${({ selected }) => (selected ? '#2F6472' : '#ccc')};
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  position: relative;
  height: 100%;
  width: 210px;
  &:hover {
    border-color: #2F6472;
  }
  @media (max-width: 600px) {
      height: 120px;
      width: 100px;
      padding: 0px;
  }
`;

const ButtonGroup = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
`;

const HousingDetailsRoomStep: React.FC = () => {
  const { t } = useTranslation('habitation');

  const dispatch = useAppDispatch();
  const floorLevel = useAppSelector((state) => state.habitation.floorLevel);

  const handleSelect = (level: FloorLevel) => {
    dispatch(setFloorLevel(level));
  };

  const handleNext = () => {
  
  dispatch(setStepVerified(3));
  dispatch(nextStep())
  };


  return (
    <Box sx={{ maxWidth: 800, margin: { xs: '0 auto', sm: '0 100px' }, padding: 2 }}>
      <Typography variant="subtitle1" gutterBottom mb={0}>
        {t('step3.title')}
      </Typography>
      <InfoText>{t('step3.info')}</InfoText>
      <Grid container spacing={2} mt={1}>
        <Grid item xs={4} sm={4} container justifyContent="center">
          <OptionBox selected={floorLevel === FloorLevel.ground} onClick={() => handleSelect(FloorLevel.ground)}>
            <FloorIcon highlightedIndex={0} />
            <Typography mt={{sm: 2, xs: 0}}>{t('step3.ground')}</Typography>
          </OptionBox>
        </Grid>
        <Grid item xs={4} sm={4} container justifyContent="center">
          <OptionBox selected={floorLevel === FloorLevel.middle} onClick={() => handleSelect(FloorLevel.middle)}>
            <FloorIcon highlightedIndex={1} />
            <Typography mt={{sm: 2, xs: 0}}>{t('step3.middle')}</Typography>
          </OptionBox>
        </Grid>
        <Grid item xs={4} sm={4} container justifyContent="center">
          <OptionBox selected={floorLevel === FloorLevel.top} onClick={() => handleSelect(FloorLevel.top)}>
            <FloorIcon highlightedIndex={2} />
            <Typography mt={{sm: 2, xs: 0}}>{t('step3.last')}</Typography>
          </OptionBox>
        </Grid>
      </Grid>

      <ButtonGroup>
        <Button onClick={() => dispatch(prevStep())} variant="outlined" sx={{ borderRadius: 20, px: 4 }}>
          {t('step1.back')}
        </Button>
        <Button
          onClick={() => handleNext()}
          variant="contained"
          sx={{
            borderRadius: 20,
            px: 4,
            backgroundColor: '#2F6472',
            '&:hover': { backgroundColor: 'white', color: '#2F6472' }
          }}
          disabled={floorLevel == null}
        >
          {t('step1.next')}
        </Button>
      </ButtonGroup>
    </Box>
  );
};

export default HousingDetailsRoomStep;
